import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Layout, Card, Table, Button, Space, Tag, Typography, Row, Col,
  Input, Select, Avatar, Tooltip, Spin, DatePicker, Divider, Empty,
  Progress, Rate, Badge, Collapse, Statistic,
} from "antd";
import {
  FaUser, FaSearch, FaTasks, FaClock, FaArrowLeft, FaDownload,
  FaCalendarAlt, FaEnvelope, FaIdCard, FaChartBar, FaPauseCircle,
  FaCheckCircle, FaTimesCircle, FaStar, FaExclamationTriangle,
  FaDollarSign, FaUserTie, FaThumbsUp, FaLayerGroup, FaBrain,
  FaHandshake, FaTools,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Api from "../api/Api";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatMoney = (v) => {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("pt-AO", { style: "currency", currency: "AOA", minimumFractionDigits: 2 }).format(v);
};

const formatHoras = (h) => {
  if (h == null || isNaN(h)) return "—";
  const hh = Math.floor(h), mm = Math.round((h - hh) * 60);
  return mm === 0 ? `${hh}h` : `${hh}h ${mm}min`;
};

const formatMinutos = (min) => {
  if (!min) return "0min";
  const h = Math.floor(min / 60), m = min % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const formatDate = (d) => d ? dayjs(d).format("DD/MM/YYYY") : "—";
const formatDateTime = (d) => d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "—";

const getStatusInfo = (id) => ({
  1: { label: "ABERTO",     color: "#1677ff", bg: "#e6f4ff" },
  2: { label: "FECHADO",    color: "#52c41a", bg: "#f6ffed" },
  3: { label: "SUSPENSO",   color: "#fa8c16", bg: "#fff7e6" },
  4: { label: "EM ANÁLISE", color: "#722ed1", bg: "#f9f0ff" },
}[id] || { label: "—", color: "#8c8c8c", bg: "#f5f5f5" });

/* classificação 1-5 → label */
const classLabel = (nota) => ({
  1: "Insuficiente", 2: "Fraco", 3: "Suficiente", 4: "Bom", 5: "Excelente"
}[nota] || "—");

const classColor = (nota) => ({
  1: "#ff4d4f", 2: "#fa8c16", 3: "#fadb14", 4: "#52c41a", 5: "#1677ff"
}[nota] || "#8c8c8c");

/* ─────────────────────────────────────────────────────────────────────────────
   METRIC CARD
───────────────────────────────────────────────────────────────────────────── */
const MetricCard = ({ title, value, icon, iconBg, iconColor, subtitle, extra }) => (
  <Card
    bordered={false}
    style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", height: "100%" }}
    bodyStyle={{ padding: "20px 24px" }}
  >
    <Space size="middle" align="start" style={{ width: "100%", justifyContent: "space-between" }}>
      <Space size="middle" align="start">
        <div style={{ backgroundColor: iconBg, borderRadius: 12, padding: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {React.cloneElement(icon, { style: { fontSize: 20, color: iconColor } })}
        </div>
        <div>
          <Text style={{ fontSize: 12, color: "#8c8c8c", display: "block" }}>{title}</Text>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1f1f1f", lineHeight: 1.2, marginTop: 4 }}>{value}</div>
          {subtitle && <Text style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2, display: "block" }}>{subtitle}</Text>}
        </div>
      </Space>
      {extra}
    </Space>
  </Card>
);

/* ─────────────────────────────────────────────────────────────────────────────
   INDICADOR DE CLASSIFICAÇÃO
───────────────────────────────────────────────────────────────────────────── */
const ClassCard = ({ label, nota, icon }) => {
  const cor = classColor(nota);
  return (
    <Card
      bordered={false}
      style={{ borderRadius: 14, border: `1.5px solid ${cor}22`, background: `${cor}08`, textAlign: "center", height: "100%" }}
      bodyStyle={{ padding: "16px 12px" }}
    >
      <div style={{ fontSize: 22, color: cor, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 8 }}>{label}</div>
      <Rate disabled defaultValue={nota || 0} style={{ fontSize: 14, color: cor }} />
      <div style={{ marginTop: 6 }}>
        <Tag style={{ background: `${cor}22`, color: cor, border: "none", borderRadius: 20, fontWeight: 600, fontSize: 11 }}>
          {classLabel(nota)}
        </Tag>
      </div>
    </Card>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   DOWNLOAD PDF  (usa window.print com estilos inline)
   Alternativa sem dependência: abre nova aba com HTML formatado e imprime
───────────────────────────────────────────────────────────────────────────── */
const downloadPDF = (user, custoData, tarefas, dateRange) => {
  if (!user) return;

  const periodo = dateRange
    ? `${dateRange[0]?.format("DD/MM/YYYY")} — ${dateRange[1]?.format("DD/MM/YYYY")}`
    : "—";

  const resumo = custoData?.resumo || {};

  const tarefasRows = (custoData?.tarefas || []).map((t) => `
    <tr>
      <td>${t.titulo || "—"}</td>
      <td>${formatDateTime(t.inicio)}</td>
      <td>${formatDateTime(t.datafecho)}</td>
      <td>${formatMinutos(t.minutos_efectivos)}</td>
      <td>${formatMinutos(t.minutos_suspensos)}</td>
      <td style="font-weight:700;color:#1677ff">${formatMoney(t.custo)}</td>
    </tr>
  `).join("");

  /* média de classificações por tarefa */
  const avgClass = tarefas.length
    ? (tarefas.reduce((a, t) => {
        const vals = [t.qualidade_entrega, t.espirito_equipa, t.iniciativa_proatividade, t.resolucao_problema].filter(Boolean);
        return a + (vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0);
      }, 0) / tarefas.length).toFixed(1)
    : "—";

  const html = `<!DOCTYPE html>
<html lang="pt">
<head>
<meta charset="UTF-8"/>
<title>Ficha Técnica — ${user.nome}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1f1f1f; background:#fff; padding: 32px; }
  .header { background: linear-gradient(135deg,#1677ff,#4096ff); color:#fff; border-radius:12px; padding:28px 32px; margin-bottom:28px; display:flex; align-items:center; gap:20px; }
  .avatar { width:64px; height:64px; border-radius:50%; background:rgba(255,255,255,0.25); display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:700; color:#fff; border:2px solid rgba(255,255,255,0.5); flex-shrink:0; }
  h1 { font-size:22px; font-weight:700; }
  .sub { font-size:13px; opacity:.85; margin-top:3px; }
  .badge { display:inline-block; background:rgba(255,255,255,0.2); border:1px solid rgba(255,255,255,0.4); border-radius:20px; padding:3px 12px; font-size:12px; margin-top:6px; }
  .section { margin-bottom:28px; }
  .section-title { font-size:15px; font-weight:700; color:#1f1f1f; border-left:4px solid #1677ff; padding-left:10px; margin-bottom:14px; }
  .metrics { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-bottom:28px; }
  .metric { background:#f8faff; border:1px solid #e6f0ff; border-radius:12px; padding:16px; text-align:center; }
  .metric-val { font-size:20px; font-weight:700; color:#1677ff; }
  .metric-lbl { font-size:11px; color:#8c8c8c; margin-top:3px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  th { background:#f5f5f5; padding:10px 12px; text-align:left; font-weight:600; color:#595959; border-bottom:1px solid #e8e8e8; }
  td { padding:10px 12px; border-bottom:1px solid #f0f0f0; vertical-align:middle; }
  tr:last-child td { border-bottom:none; }
  .class-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
  .class-box { background:#f8f8f8; border-radius:10px; padding:12px; text-align:center; }
  .class-lbl { font-size:11px; color:#8c8c8c; margin-bottom:4px; }
  .class-val { font-size:15px; font-weight:700; }
  .footer { margin-top:32px; border-top:1px solid #e8e8e8; padding-top:14px; font-size:11px; color:#8c8c8c; display:flex; justify-content:space-between; }
  @media print { body { padding:16px; } }
</style>
</head>
<body>
<div class="header">
  <div class="avatar">${user.nome?.charAt(0)}</div>
  <div>
    <h1>${user.nome}</h1>
    <div class="sub">${user.funcao || "—"} &nbsp;·&nbsp; ${user.email || "—"} &nbsp;·&nbsp; ${user.employee_id || "—"}</div>
    <span class="badge">Salário/hora: ${formatMoney(user.salario_hora)}</span>
    &nbsp;
    <span class="badge">Período: ${periodo}</span>
  </div>
</div>

<div class="metrics">
  <div class="metric"><div class="metric-val">${formatMoney(resumo.custo_total)}</div><div class="metric-lbl">Custo Total</div></div>
  <div class="metric"><div class="metric-val">${resumo.formatado || "—"}</div><div class="metric-lbl">Horas Efectivas</div></div>
  <div class="metric"><div class="metric-val">${resumo.total_tarefas || 0}</div><div class="metric-lbl">Tarefas Concluídas</div></div>
</div>

<div class="section">
  <div class="section-title">Tarefas no Período</div>
  <table>
    <thead><tr>
      <th>Tarefa</th><th>Início</th><th>Fecho</th><th>T. Efectivo</th><th>T. Suspenso</th><th>Custo</th>
    </tr></thead>
    <tbody>${tarefasRows || '<tr><td colspan="6" style="text-align:center;color:#8c8c8c;padding:20px">Sem tarefas no período</td></tr>'}</tbody>
  </table>
</div>

<div class="section">
  <div class="section-title">Avaliação Média das Tarefas</div>
  <div class="class-grid">
    <div class="class-box"><div class="class-lbl">Qualidade de Entrega</div><div class="class-val" style="color:#1677ff">${classLabel(Math.round(tarefas.reduce((a,t)=>a+(t.qualidade_entrega||0),0)/Math.max(tarefas.length,1)))}</div></div>
    <div class="class-box"><div class="class-lbl">Espírito de Equipa</div><div class="class-val" style="color:#52c41a">${classLabel(Math.round(tarefas.reduce((a,t)=>a+(t.espirito_equipa||0),0)/Math.max(tarefas.length,1)))}</div></div>
    <div class="class-box"><div class="class-lbl">Iniciativa & Proactividade</div><div class="class-val" style="color:#722ed1">${classLabel(Math.round(tarefas.reduce((a,t)=>a+(t.iniciativa_proatividade||0),0)/Math.max(tarefas.length,1)))}</div></div>
    <div class="class-box"><div class="class-lbl">Resolução de Problemas</div><div class="class-val" style="color:#fa8c16">${classLabel(Math.round(tarefas.reduce((a,t)=>a+(t.resolucao_problema||0),0)/Math.max(tarefas.length,1)))}</div></div>
  </div>
  <div style="margin-top:12px;font-size:12px;color:#595959">Média geral de desempenho: <strong>${avgClass} / 5</strong></div>
</div>

<div class="footer">
  <span>Documento gerado automaticamente pelo sistema ERP</span>
  <span>${dayjs().format("DD/MM/YYYY HH:mm")}</span>
</div>

<script>window.onload=()=>{ window.print(); }</script>
</body>
</html>`;

  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
};

/* ─────────────────────────────────────────────────────────────────────────────
   TELA PRINCIPAL
───────────────────────────────────────────────────────────────────────────── */
const FichaTecnicaColaborador = () => {
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loadingColabs, setLoadingColabs] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const [custoData, setCustoData] = useState(null);
  const [tarefas, setTarefas] = useState([]);          // tarefas com classificações
  const [rejeicoes, setRejeicoes] = useState({});      // { id_tarefa: count }
  const [loading, setLoading] = useState(false);

  const dataInicio = dateRange?.[0]?.format("YYYY-MM-DD");
  const dataFim    = dateRange?.[1]?.format("YYYY-MM-DD");

  /* ── Carrega lista de colaboradores ── */
  useEffect(() => {
    const fetch = async () => {
      try {
        setLoadingColabs(true);
        const res = await Api.ListUser();
        const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setColaboradores(arr);
      } catch { /* silencioso */ }
      finally { setLoadingColabs(false); }
    };
    fetch();
    document.title = "Ficha Técnica do Colaborador";
  }, []);

  /* ── Carrega dados do colaborador seleccionado ── */
  const loadUserData = useCallback(async (userId) => {
    if (!userId || !dataInicio || !dataFim) return;

    const user = colaboradores.find((c) => c.id === userId);
    setUserInfo(user || null);

    try {
      setLoading(true);
      setCustoData(null);
      setTarefas([]);
      setRejeicoes({});

      // 1. Custos e horas trabalhadas
      const custoRes = await Api.ListCustoUsuario({ id: userId, dataInicio, dataFim });
      const custo = custoRes?.data || null;
      setCustoData(custo);

      // 2. Tarefas (com classificações) — filtramos pelo responsável
      const tarefasRes = await Api.ListTarefa(user?.direcao);
      const todasTarefas = Array.isArray(tarefasRes) ? tarefasRes : [];

      // Verificar responsabilidade: busca responsáveis de cada tarefa
      const comResp = await Promise.allSettled(
        todasTarefas.map((t) => Api.ListResponsavelTarefa({ id: t.id }))
      );

      const minhasTarefas = todasTarefas.filter((t, i) => {
        if (comResp[i].status !== "fulfilled") return false;
        const resp = Array.isArray(comResp[i].value) ? comResp[i].value : [];
        return resp.some((r) => r.id === userId);
      });

      // Filtra pelo período
      const tarefasPeriodo = minhasTarefas.filter((t) => {
        if (!t.inicio) return false;
        const ini = dayjs(t.inicio);
        return ini.isAfter(dayjs(dataInicio).subtract(1, "day")) && ini.isBefore(dayjs(dataFim).add(1, "day"));
      });

      setTarefas(tarefasPeriodo);

      // 3. Rejeições por tarefa
      const rejRes = await Promise.allSettled(
        tarefasPeriodo.map((t) => Api.ListRejeicaoTarefa({ id: t.id }))
      );

      const rejMap = {};
      tarefasPeriodo.forEach((t, i) => {
        rejMap[t.id] = rejRes[i].status === "fulfilled"
          ? (Array.isArray(rejRes[i].value) ? rejRes[i].value.length : 0)
          : 0;
      });
      setRejeicoes(rejMap);

    } catch (e) {
      toast.error("Erro ao carregar dados do colaborador.");
    } finally {
      setLoading(false);
    }
  }, [colaboradores, dataInicio, dataFim]);

  const handleSelect = (val) => {
    setSelectedUser(val);
    loadUserData(val);
  };

  useEffect(() => {
    if (selectedUser) loadUserData(selectedUser);
  }, [dataInicio, dataFim]);

  /* ── Métricas derivadas ── */
  const totalRejeicoes = Object.values(rejeicoes).reduce((a, v) => a + v, 0);
  const tarefasConcluidas = tarefas.filter((t) => t.id_status === 2).length;
  const tarefasAbertas   = tarefas.filter((t) => t.id_status === 1).length;
  const tarefasSuspensas = tarefas.filter((t) => t.id_status === 3).length;

  const avgNota = (campo) => {
    const vals = tarefas.map((t) => t[campo]).filter(Boolean);
    return vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : 0;
  };

  const avgGeral = ["qualidade_entrega","espirito_equipa","iniciativa_proatividade","resolucao_problema"]
    .reduce((a, c) => a + avgNota(c), 0) / 4;

  /* ── Colunas tabela de tarefas ── */
  const columnsTarefas = [
    {
      title: "Tarefa",
      dataIndex: "titulo",
      key: "titulo",
      width: 200,
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.etapa}</Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "id_status",
      key: "status",
      width: 110,
      render: (v) => {
        const s = getStatusInfo(v);
        return (
          <Tag style={{ background: s.bg, color: s.color, border: "none", borderRadius: 20, fontWeight: 600, fontSize: 11 }}>
            {s.label}
          </Tag>
        );
      },
    },
    {
      title: "Progresso",
      dataIndex: "percentagem",
      key: "perc",
      width: 130,
      render: (v) => (
        <Space>
          <Progress percent={v || 0} size="small" status={v === 100 ? "success" : "active"} style={{ width: 80 }} strokeColor={{ "0%": "#1677ff", "100%": "#52c41a" }} />
          <Text style={{ fontSize: 11 }}>{v || 0}%</Text>
        </Space>
      ),
    },
    {
      title: "Início / Fecho",
      key: "datas",
      width: 160,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 11 }}><FaCalendarAlt style={{ color: "#1677ff", marginRight: 4 }} />{formatDate(r.inicio)}</Text>
          <Text style={{ fontSize: 11 }}><FaCheckCircle style={{ color: "#52c41a", marginRight: 4 }} />{formatDate(r.datafecho)}</Text>
        </Space>
      ),
    },
    {
      title: "Horas / Custo",
      key: "horas",
      width: 140,
      render: (_, r) => {
        const td = custoData?.tarefas?.find((ct) => ct.id_tarefa === r.id);
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}><FaClock style={{ color: "#1677ff", marginRight: 4 }} />{td ? td.formatado : "—"}</Text>
            <Text style={{ fontSize: 12, fontWeight: 700, color: "#1677ff" }}>{td ? formatMoney(td.custo) : "—"}</Text>
          </Space>
        );
      },
    },
    {
      title: "Rejeições",
      key: "rej",
      width: 90,
      align: "center",
      render: (_, r) => {
        const c = rejeicoes[r.id] || 0;
        return c > 0
          ? <Tag color="error" style={{ borderRadius: 20 }}><FaTimesCircle style={{ marginRight: 4 }} />{c}</Tag>
          : <Tag color="success" style={{ borderRadius: 20 }}><FaCheckCircle style={{ marginRight: 4 }} />0</Tag>;
      },
    },
    {
      title: "Avaliação",
      key: "aval",
      width: 100,
      align: "center",
      render: (_, r) => {
        const vals = [r.qualidade_entrega, r.espirito_equipa, r.iniciativa_proatividade, r.resolucao_problema].filter(Boolean);
        if (!vals.length) return <Text type="secondary" style={{ fontSize: 11 }}>Sem avaliação</Text>;
        const avg = vals.reduce((a, v) => a + v, 0) / vals.length;
        return (
          <Tooltip title={`Qualidade: ${r.qualidade_entrega} | Equipa: ${r.espirito_equipa} | Iniciativa: ${r.iniciativa_proatividade} | Resolução: ${r.resolucao_problema}`}>
            <Rate disabled allowHalf defaultValue={avg} style={{ fontSize: 12, color: classColor(Math.round(avg)) }} />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* ── HEADER ── */}
        <div style={{
          background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
          borderRadius: 16, padding: "24px 32px", marginBottom: 32,
          boxShadow: "0 8px 20px rgba(22,119,255,0.2)",
        }}>
          <Row align="middle" justify="space-between" gutter={[16, 16]}>
            <Col>
              <Space size="middle">
                <div style={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 12, padding: 10, display: "flex", alignItems: "center" }}>
                  <FaUserTie style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>Ficha Técnica do Colaborador</Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
                    Análise de desempenho, tarefas, horas e custos
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Row gutter={12} align="middle">
                <Col>
                  <Space direction="vertical" size={2}>
                    <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
                      <FaCalendarAlt style={{ marginRight: 4 }} />Período
                    </Text>
                    <RangePicker
                      value={dateRange}
                      onChange={setDateRange}
                      format="DD/MM/YYYY"
                      style={{ borderRadius: 20 }}
                      allowClear={false}
                    />
                  </Space>
                </Col>
                <Col>
                  <Button
                    size="large"
                    icon={<FaArrowLeft />}
                    onClick={() => navigate(-1)}
                    style={{ backgroundColor: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 30 }}
                  >
                    Voltar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        {/* ── SELECTOR ── */}
        <Card
          bordered={false}
          style={{ borderRadius: 16, marginBottom: 24, background: "#f8faff", border: "1px solid #e6f0ff", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
          bodyStyle={{ padding: "20px 24px" }}
        >
          <Row align="middle" gutter={[16, 12]}>
            <Col xs={24} md={14}>
              <Space style={{ width: "100%" }} direction="vertical" size={4}>
                <Text style={{ fontSize: 12, color: "#8c8c8c" }}>Seleccione o colaborador</Text>
                <Select
                  showSearch
                  placeholder="Pesquisar colaborador..."
                  style={{ width: "100%" }}
                  value={selectedUser}
                  onChange={handleSelect}
                  loading={loadingColabs}
                  size="large"
                  optionFilterProp="children"
                  filterOption={(input, opt) => opt?.children?.toLowerCase().includes(input.toLowerCase())}
                  suffixIcon={<FaSearch style={{ color: "#bfbfbf" }} />}
                >
                  {colaboradores.map((c) => (
                    <Option key={c.id} value={c.id}>
                      <Space>
                        <Avatar size="small" style={{ backgroundColor: "#1677ff", fontSize: 10 }}>{c.nome?.charAt(0)}</Avatar>
                        {c.nome} <Text type="secondary" style={{ fontSize: 11 }}>· {c.funcao}</Text>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Space>
            </Col>
            {userInfo && (
              <Col xs={24} md={10}>
                <Space wrap>
                  <Tag icon={<FaIdCard />} color="blue" style={{ borderRadius: 20, padding: "4px 12px" }}>{userInfo.employee_id}</Tag>
                  <Tag icon={<FaEnvelope />} color="cyan" style={{ borderRadius: 20, padding: "4px 12px" }}>{userInfo.email}</Tag>
                  <Tag color="purple" style={{ borderRadius: 20, padding: "4px 12px" }}>{userInfo.perfil}</Tag>
                </Space>
              </Col>
            )}
          </Row>
        </Card>

        {!selectedUser && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text style={{ color: "#8c8c8c" }}>Seleccione um colaborador para ver a ficha técnica</Text>}
            style={{ padding: "60px 0" }}
          />
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: 80 }}>
            <Spin size="large" tip="A carregar ficha técnica..." />
          </div>
        )}

        {selectedUser && !loading && userInfo && (
          <>
            {/* ── PROFILE CARD ── */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, marginBottom: 24, background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)", boxShadow: "0 8px 24px rgba(22,119,255,0.25)" }}
              bodyStyle={{ padding: "28px 32px" }}
            >
              <Row align="middle" justify="space-between" gutter={[24, 16]}>
                <Col>
                  <Space size="large" align="center">
                    <Avatar
                      size={72}
                      style={{ background: "rgba(255,255,255,0.25)", fontSize: 30, fontWeight: 700, border: "2px solid rgba(255,255,255,0.5)" }}
                    >
                      {userInfo.nome?.charAt(0)}
                    </Avatar>
                    <div>
                      <Title level={3} style={{ color: "#fff", margin: 0 }}>{userInfo.nome}</Title>
                      <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
                        {userInfo.funcao} · {userInfo.email}
                      </Text>
                      <div style={{ marginTop: 8 }}>
                        <Space wrap>
                          <Tag style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20 }}>
                            <FaIdCard style={{ marginRight: 4 }} />{userInfo.employee_id}
                          </Tag>
                          <Tag style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20 }}>
                            <FaDollarSign style={{ marginRight: 4 }} />{formatMoney(userInfo.salario_hora)}/hora
                          </Tag>
                          <Tag style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20 }}>
                            {userInfo.perfil}
                          </Tag>
                        </Space>
                      </div>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <Button
                    type="default"
                    size="large"
                    icon={<FaDownload />}
                    onClick={() => downloadPDF(userInfo, custoData, tarefas, dateRange)}
                    style={{ backgroundColor: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 30, fontWeight: 600 }}
                  >
                    Exportar PDF
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* ── MÉTRICAS PRINCIPAIS ── */}
            <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Custo Total"
                  value={formatMoney(custoData?.resumo?.custo_total)}
                  icon={<FaDollarSign />}
                  iconBg="#e6fffb" iconColor="#13c2c2"
                  subtitle="No período selecionado"
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Horas Efectivas"
                  value={custoData?.resumo?.formatado || "—"}
                  icon={<FaClock />}
                  iconBg="#e6f4ff" iconColor="#1677ff"
                  subtitle={formatMinutos(custoData?.resumo?.minutos_efectivos)}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Tarefas no Período"
                  value={tarefas.length}
                  icon={<FaTasks />}
                  iconBg="#fff7e6" iconColor="#fa8c16"
                  subtitle={`${tarefasConcluidas} concluídas · ${tarefasAbertas} abertas`}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <MetricCard
                  title="Rejeições"
                  value={totalRejeicoes}
                  icon={<FaTimesCircle />}
                  iconBg={totalRejeicoes > 0 ? "#fff1f0" : "#f6ffed"}
                  iconColor={totalRejeicoes > 0 ? "#ff4d4f" : "#52c41a"}
                  subtitle="Total de revisões pedidas"
                />
              </Col>
            </Row>

            {/* ── STATUS DAS TAREFAS ── */}
            <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 14, border: "1px solid #f6ffed", background: "#f6ffed", textAlign: "center" }} bodyStyle={{ padding: 16 }}>
                  <FaCheckCircle style={{ fontSize: 24, color: "#52c41a", marginBottom: 6 }} />
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#52c41a" }}>{tarefasConcluidas}</div>
                  <Text style={{ color: "#52c41a", fontSize: 12 }}>Concluídas</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 14, border: "1px solid #e6f4ff", background: "#e6f4ff", textAlign: "center" }} bodyStyle={{ padding: 16 }}>
                  <FaTasks style={{ fontSize: 24, color: "#1677ff", marginBottom: 6 }} />
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#1677ff" }}>{tarefasAbertas}</div>
                  <Text style={{ color: "#1677ff", fontSize: 12 }}>Em Curso</Text>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ borderRadius: 14, border: "1px solid #fff7e6", background: "#fff7e6", textAlign: "center" }} bodyStyle={{ padding: 16 }}>
                  <FaPauseCircle style={{ fontSize: 24, color: "#fa8c16", marginBottom: 6 }} />
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#fa8c16" }}>{tarefasSuspensas}</div>
                  <Text style={{ color: "#fa8c16", fontSize: 12 }}>Suspensas</Text>
                </Card>
              </Col>
            </Row>

            {/* ── AVALIAÇÕES MÉDIAS ── */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
              title={
                <Space>
                  <FaStar style={{ color: "#fadb14" }} />
                  <span style={{ fontWeight: 600 }}>Avaliação de Desempenho</span>
                  <Tag color="blue" style={{ borderRadius: 20 }}>
                    Média geral: {avgGeral > 0 ? avgGeral.toFixed(1) : "—"} / 5
                  </Tag>
                </Space>
              }
            >
              {tarefas.length === 0 ? (
                <Empty description="Sem avaliações no período" />
              ) : (
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <ClassCard label="Qualidade de Entrega" nota={Math.round(avgNota("qualidade_entrega"))} icon={<FaThumbsUp />} />
                  </Col>
                  <Col xs={12} sm={6}>
                    <ClassCard label="Espírito de Equipa" nota={Math.round(avgNota("espirito_equipa"))} icon={<FaHandshake />} />
                  </Col>
                  <Col xs={12} sm={6}>
                    <ClassCard label="Iniciativa & Proactividade" nota={Math.round(avgNota("iniciativa_proatividade"))} icon={<FaBrain />} />
                  </Col>
                  <Col xs={12} sm={6}>
                    <ClassCard label="Resolução de Problemas" nota={Math.round(avgNota("resolucao_problema"))} icon={<FaTools />} />
                  </Col>
                </Row>
              )}
            </Card>

            {/* ── TABELA DE TAREFAS ── */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}
              title={
                <Space>
                  <FaTasks style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 600 }}>Tarefas no Período</span>
                  <Badge count={tarefas.length} style={{ backgroundColor: "#1677ff" }} />
                </Space>
              }
              bodyStyle={{ padding: 0 }}
            >
              <Table
                columns={columnsTarefas}
                dataSource={tarefas}
                rowKey="id"
                pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total: ${t} tarefas`, style: { padding: "16px 24px" } }}
                locale={{ emptyText: "Sem tarefas no período" }}
                size="middle"
                scroll={{ x: 1100 }}
                expandable={{
                  expandedRowRender: (record) => {
                    const td = custoData?.tarefas?.find((ct) => ct.id_tarefa === record.id);
                    const rejs = rejeicoes[record.id] || 0;
                    return (
                      <div style={{ padding: "16px 24px", background: "#fafafa", borderRadius: 10 }}>
                        <Row gutter={[24, 16]}>
                          {/* Suspensões */}
                          <Col xs={24} md={12}>
                            <Text strong style={{ fontSize: 12, color: "#fa8c16", display: "block", marginBottom: 8 }}>
                              <FaPauseCircle style={{ marginRight: 6 }} />
                              Suspensões ({td?.suspensoes?.length || 0})
                            </Text>
                            {td?.suspensoes?.length > 0 ? (
                              <Table
                                size="small"
                                pagination={false}
                                columns={[
                                  { title: "Descrição", dataIndex: "descricao", key: "d" },
                                  { title: "Entrada", dataIndex: "entrada", key: "e", render: (v) => formatDateTime(v) },
                                  { title: "Saída",   dataIndex: "saida",   key: "s", render: (v) => formatDateTime(v) },
                                  { title: "Min.",    dataIndex: "minutos", key: "m", render: (v) => `${v}min` },
                                ]}
                                dataSource={td.suspensoes}
                                rowKey="id"
                                style={{ background: "#fff", borderRadius: 8 }}
                              />
                            ) : (
                              <Text type="secondary" style={{ fontSize: 12 }}>Sem suspensões</Text>
                            )}
                          </Col>

                          {/* Rejeições */}
                          <Col xs={24} md={12}>
                            <Text strong style={{ fontSize: 12, color: "#ff4d4f", display: "block", marginBottom: 8 }}>
                              <FaTimesCircle style={{ marginRight: 6 }} />
                              Rejeições ({rejs})
                            </Text>
                            {rejs > 0 ? (
                              <div style={{ background: "#fff1f0", borderRadius: 8, padding: "10px 14px" }}>
                                <Text style={{ fontSize: 12, color: "#ff4d4f" }}>
                                  Esta tarefa foi rejeitada {rejs} vez{rejs > 1 ? "es" : ""}.
                                </Text>
                              </div>
                            ) : (
                              <div style={{ background: "#f6ffed", borderRadius: 8, padding: "10px 14px" }}>
                                <Text style={{ fontSize: 12, color: "#52c41a" }}>
                                  <FaCheckCircle style={{ marginRight: 6 }} />Sem rejeições registadas
                                </Text>
                              </div>
                            )}

                            {/* Classificações individuais */}
                            {(record.qualidade_entrega || record.espirito_equipa) && (
                              <div style={{ marginTop: 12 }}>
                                <Text strong style={{ fontSize: 12, color: "#595959", display: "block", marginBottom: 8 }}>Avaliação desta tarefa</Text>
                                <Row gutter={[8, 8]}>
                                  {[
                                    ["Qualidade", record.qualidade_entrega],
                                    ["Equipa",    record.espirito_equipa],
                                    ["Iniciativa",record.iniciativa_proatividade],
                                    ["Resolução", record.resolucao_problema],
                                  ].map(([lbl, nota]) => (
                                    <Col key={lbl} xs={12}>
                                      <div style={{ background: "#fff", border: "1px solid #f0f0f0", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                                        <div style={{ fontSize: 10, color: "#8c8c8c" }}>{lbl}</div>
                                        <Rate disabled defaultValue={nota || 0} style={{ fontSize: 10, color: classColor(nota) }} />
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              </div>
                            )}
                          </Col>
                        </Row>

                        {/* Recomendação */}
                        {record.recomencao && (
                          <div style={{ marginTop: 14, background: "#f0f9ff", borderRadius: 10, padding: "12px 16px", borderLeft: "3px solid #1677ff" }}>
                            <Text strong style={{ fontSize: 12, color: "#1677ff", display: "block", marginBottom: 4 }}>
                              <FaChartBar style={{ marginRight: 6 }} />Recomendação
                            </Text>
                            <Text style={{ fontSize: 13, color: "#1f1f1f" }}>{record.recomencao}</Text>
                          </div>
                        )}
                      </div>
                    );
                  },
                }}
                summary={(pageData) => {
                  const total = pageData.reduce((a, r) => {
                    const td = custoData?.tarefas?.find((ct) => ct.id_tarefa === r.id);
                    return a + (td?.custo || 0);
                  }, 0);
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row style={{ background: "#f0f9ff" }}>
                        <Table.Summary.Cell index={0} colSpan={6}>
                          <Text strong>Total da página</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">
                          <Text strong style={{ color: "#1677ff", fontSize: 15 }}>{formatMoney(total)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Card>
          </>
        )}
      </Content>
    </Layout>
  );
};

export default FichaTecnicaColaborador;